import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, PageHeader, SkeletonRows, useToast } from "../../components/ui";
import type { BadgeTone } from "../../components/ui";
import {
  observabilityService,
  type HealthComponent,
  type HealthStatus,
  type SystemHealth,
} from "../../services/observability.service";

const TONES: Record<HealthStatus, BadgeTone> = { ok: "ok", warning: "warn", down: "danger" };
const LABELS: Record<HealthStatus, string> = { ok: "OK", warning: "Warning", down: "Down" };

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return `${value.length} item(ns)`;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function ComponentCard({ component }: { component: HealthComponent }) {
  const providers = Array.isArray(component.details.providers)
    ? (component.details.providers as Array<Record<string, unknown>>)
    : null;

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">{component.label}</h2>
        <Badge tone={TONES[component.status]} dot>
          {LABELS[component.status]}
        </Badge>
      </div>
      <dl className="mt-4 space-y-2 text-xs">
        {Object.entries(component.details)
          .filter(([key]) => key !== "providers")
          .map(([key, value]) => (
            <div key={key} className="flex items-start justify-between gap-4">
              <dt className="font-mono text-[11px] uppercase tracking-wide text-ink-muted">{key}</dt>
              <dd className="max-w-[60%] break-words text-right text-ink">{renderValue(value)}</dd>
            </div>
          ))}
      </dl>
      {providers && (
        <ul className="mt-4 space-y-2 border-t border-line/40 pt-3 text-xs">
          {providers.map((provider, index) => (
            <li key={index} className="flex items-center justify-between gap-3">
              <span className="text-ink">{String(provider.name ?? provider.provider ?? "provider")}</span>
              <span className="font-mono text-[11px] text-ink-muted">{String(provider.status ?? "unknown")}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function SystemHealthPage() {
  const toast = useToast();
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHealth(await observabilityService.system());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível verificar o sistema.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sistema"
        description="Estado dos componentes da plataforma: backend, worker, PostgreSQL, runner, Docker, armazenamento e provedores de IA."
        actions={
          <Button variant="secondary" onClick={() => void load()}>
            Verificar agora
          </Button>
        }
      />

      {loading && <SkeletonRows rows={4} />}

      {!loading && health && (
        <>
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink">Estado geral</p>
                <p className="mt-1 text-xs text-ink-muted">
                  Verificado em {new Date(health.checked_at).toLocaleString("pt-BR")}
                </p>
              </div>
              <Badge tone={TONES[health.status]} dot>
                {LABELS[health.status]}
              </Badge>
            </div>
          </Card>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {health.components.map((component) => (
              <ComponentCard key={component.key} component={component} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
